import { QueryClient, queryOptions } from '@tanstack/react-query'

import { getConfig } from '@/app/config'
import { warn } from '@/utils/logger'

const AUTH0_LOGIN_FLAG_PATH = '/api/user-service/api/ship-users/feature-flags/auth0-login'
const LOOKUP_TIMEOUT_MS = 3_000
const LOOKUP_STALE_TIME_MS = 1000 * 60 * 5

/**
 * Unauthenticated lookup of the Auth0 login flag for one customer. `/login` has no token to
 * authenticate the user-profile-core flag search with, so user-service exposes this endpoint and
 * evaluates the flag for the customer id Ship stored at the previous login.
 */
const getAuth0LoginFlag = async (customerId: string): Promise<boolean> => {
  const url = `${getConfig().API_ENDPOINT ?? ''}${AUTH0_LOGIN_FLAG_PATH}?customerId=${encodeURIComponent(customerId)}`
  const response = await fetch(url, { signal: AbortSignal.timeout(LOOKUP_TIMEOUT_MS) })

  if (!response.ok) {
    throw new Error(`Auth0 login flag lookup responded with ${response.status}`)
  }

  const { enabled } = (await response.json()) as { enabled?: boolean }

  return enabled === true
}

export const auth0LoginFlagQueryOptions = (customerId: string) =>
  queryOptions({
    queryKey: ['auth0LoginFlag', customerId],
    queryFn: () => getAuth0LoginFlag(customerId),
    staleTime: LOOKUP_STALE_TIME_MS,
    // The caller is a route guard a visitor is waiting on, and the v1 form is a fine answer
    retry: false,
  })

/**
 * Resolves false rather than rejecting: a customer whose flag cannot be read keeps the v1 login
 * form, the safe fallback while Auth0 login is being rolled out.
 */
export const isAuth0LoginEnabledForCustomer = async (
  customerId: string,
  queryClient?: QueryClient
): Promise<boolean> => {
  try {
    return queryClient
      ? await queryClient.query({ ...auth0LoginFlagQueryOptions(customerId), staleTime: 'static' })
      : await getAuth0LoginFlag(customerId)
  } catch (error) {
    warn('Auth0 login flag lookup failed, falling back to the v1 login form', error)
    return false
  }
}
